import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const initial = { name: "", email: "", phone: "", phone_prefix: "+41", password: "", vehicle_type: "auto" };

export default function AdminDriverCreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const close = () => {
    if (saving) return;
    setForm(initial);
    setCreated(null);
    onClose();
  };
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/drivers", form);
      setCreated(data);
      setForm(initial);
      onCreated();
      toast.success("Account driver creato, autorizzazione da completare");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Creazione non riuscita. Verifica l'elenco prima di riprovare.");
    } finally {
      setSaving(false);
    }
  };
  const copyId = async () => {
    try { await navigator.clipboard.writeText(created.id); toast.success("ID copiato"); }
    catch { toast.error("Copia l'ID dal campo qui sotto."); }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) close(); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto w-[calc(100%_-_2rem)] rounded-2xl" data-testid="admin-driver-create-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo driver interno</DialogTitle>
          <DialogDescription>L'account richiede autorizzazione interna e verifica prima di accedere. La remunerazione è gestita fuori da Stripe.</DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-4" data-testid="admin-driver-create-success">
            <p className="font-semibold">Autorizzazione interna da completare</p>
            <p className="text-sm">{created.name} è stato creato. Fai inserire il suo ID nella lista autorizzata della flotta, quindi completa la verifica dalla scheda driver. Non sono stati inviati messaggi.</p>
            <label className="block text-sm font-semibold">ID driver
              <input readOnly value={created.id} className="input-tall mt-1 w-full text-xs" onFocus={(e) => e.target.select()} data-testid="admin-driver-created-id" />
            </label>
            <button type="button" onClick={copyId} className="btn-ghost w-full">Copia ID</button>
            <Link to={`/admin/drivers/${created.id}`} onClick={close} className="btn-primary w-full justify-center">Apri scheda driver</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-sm font-semibold">Nome e cognome
              <input required maxLength={120} autoComplete="name" value={form.name} onChange={(e) => update("name", e.target.value)} className="input-tall mt-1 w-full" data-testid="create-driver-name" />
            </label>
            <label className="block text-sm font-semibold">Email
              <input required type="email" autoComplete="off" value={form.email} onChange={(e) => update("email", e.target.value)} className="input-tall mt-1 w-full" data-testid="create-driver-email" />
            </label>
            <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-2">
              <label className="block text-sm font-semibold">Prefisso
                <input required value={form.phone_prefix} onChange={(e) => update("phone_prefix", e.target.value)} className="input-tall mt-1 w-full" data-testid="create-driver-prefix" />
              </label>
              <label className="block text-sm font-semibold">Telefono
                <input required type="tel" autoComplete="off" maxLength={40} value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input-tall mt-1 w-full" data-testid="create-driver-phone" />
              </label>
            </div>
            <label className="block text-sm font-semibold">Veicolo
              <select value={form.vehicle_type} onChange={(e) => update("vehicle_type", e.target.value)} className="input-tall mt-1 w-full" data-testid="create-driver-vehicle">
                <option value="moto">Moto</option><option value="scooter">Scooter</option><option value="auto">Auto</option><option value="auto_grande">Auto grande</option><option value="furgone">Furgone</option>
              </select>
            </label>
            <label className="block text-sm font-semibold">Password iniziale
              <input required type="password" autoComplete="new-password" minLength={8} maxLength={72} value={form.password} onChange={(e) => update("password", e.target.value)} className="input-tall mt-1 w-full" data-testid="create-driver-password" />
            </label>
            <p className="text-xs text-dapnow-muted">Almeno 8 caratteri, con lettera, numero e simbolo. Comunicala personalmente al driver selezionato. Nessuna email automatica.</p>
            <button disabled={saving} className="btn-primary w-full justify-center" data-testid="create-driver-submit">{saving ? "Creazione…" : "Crea account driver"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
